import { log } from './logger'

// Which IndexedDB database failed (#536). WebKit's "Database deleted by request
// of the user" & co. carry no stack, and Firestore, Installations, Analytics and
// the search index all open their own databases — wrapping indexedDB.open is the
// only way to see the Firebase-internal ones. Side-effect import, first in
// firebase.ts so it's installed before anything opens a database.
type IdbOutcome = 'pending' | 'success' | 'error' | 'blocked'
interface IdbOpenRecord { outcome: IdbOutcome; msSinceLoad: number; error?: string; closedAbnormally?: boolean }

const opens = new Map<string, IdbOpenRecord>()

const normalizeName = (name: unknown) => String(name).slice(0, 60)

if (typeof indexedDB !== 'undefined' && typeof IDBFactory !== 'undefined') {
  const original = IDBFactory.prototype.open
  IDBFactory.prototype.open = function (this: IDBFactory, ...args: Parameters<IDBFactory['open']>) {
    const req = original.apply(this, args)
    try {
      const db = normalizeName(args[0])
      const record: IdbOpenRecord = { outcome: 'pending', msSinceLoad: Math.round(performance.now()) }
      opens.set(db, record)
      req.addEventListener('success', () => {
        try {
          record.outcome = 'success'
          // 'close' fires only when the browser closes the connection itself,
          // e.g. WebKit deleting the database out from under the page.
          req.result.addEventListener('close', () => {
            record.closedAbnormally = true
            log('idb', 'connection closed abnormally', { db })
          })
        } catch { /* diagnostics must never break an open */ }
      })
      req.addEventListener('error', () => {
        try {
          record.outcome = 'error'
          record.error = `${req.error?.name}: ${req.error?.message}`
          log('idb', 'open failed', { db, error: record.error })
        } catch { /* diagnostics must never break an open */ }
      })
      req.addEventListener('blocked', () => {
        record.outcome = 'blocked'
        log('idb', 'open blocked', { db })
      })
    } catch { /* diagnostics must never break an open */ }
    return req
  }
}

// How long a still-unresolved open of a database whose name starts with
// `prefix` has been pending, or null when there is none.
export function pendingOpenMs(prefix: string): number | null {
  for (const [name, r] of opens) {
    if (name.startsWith(prefix) && r.outcome === 'pending') return Math.round(performance.now()) - r.msSinceLoad
  }
  return null
}

export function idbDiagnosticTags(): Record<string, string> {
  const names = (match: (r: IdbOpenRecord) => boolean) =>
    [...opens].filter(([, r]) => match(r)).map(([name]) => name).join(',')
  const tags: Record<string, string> = {
    idb_failed: names(r => r.outcome === 'error' || r.outcome === 'blocked'),
    idb_pending: names(r => r.outcome === 'pending'),
    idb_closed: names(r => !!r.closedAbnormally),
  }
  return Object.fromEntries(Object.entries(tags).filter(([, v]) => v))
}
